import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Database, BarChart2, Code, FileCode } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './BlockchainProjectCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './BlockchainProjectCostCalculatorPage/components/ResultsOverview';
import CostBreakdown from './BlockchainProjectCostCalculatorPage/components/CostBreakdown';
import QuickAdjustmentSidebar from './BlockchainProjectCostCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './BlockchainProjectCostCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './BlockchainProjectCostCalculatorPage/components/TipsSidebar';
import InfoContent from './BlockchainProjectCostCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  projectType: 'dapp',
  projectSize: 'medium',
  blockchainPlatform: 'ethereum',
  customTokenRequired: false,
  smartContracts: '3',
  smartContractsComplexity: 'medium',
  frontendComplexity: 'medium',
  backendIntegration: true,
  securityAudit: true,
  testingLevel: 'comprehensive',
  deploymentSupport: true,
  maintenanceMonths: '6',
  teamLocation: 'mixed',
  timeConstraint: 'normal',
  projectDuration: '4',
  includeAdditionalFeatures: false,
  featuresKYC: false,
  featuresMultiSig: false,
  featuresDAO: false,
  featuresNFT: false,
  featuresDEX: false,
  customUIRequirements: 'standard',
};

// Определение типов блокчейн-проектов
export const projectTypes = [
  { id: 'dapp', name: 'Децентрализованное приложение (DApp)', defaultComplexity: 'medium' },
  { id: 'defi', name: 'DeFi платформа', defaultComplexity: 'high' },
  { id: 'token', name: 'Токен/Криптовалюта', defaultComplexity: 'low' },
  { id: 'nft', name: 'NFT маркетплейс', defaultComplexity: 'medium' },
  { id: 'dao', name: 'DAO/Управление', defaultComplexity: 'high' },
  { id: 'private', name: 'Частный/Корпоративный блокчейн', defaultComplexity: 'high' },
  { id: 'exchange', name: 'Децентрализованная биржа (DEX)', defaultComplexity: 'very_high' }
];

// Определение блокчейн-платформ
export const blockchainPlatforms = [
  { id: 'ethereum', name: 'Ethereum', costFactor: 1.2, language: 'Solidity' },
  { id: 'binance', name: 'Binance Smart Chain (BSC)', costFactor: 1.0, language: 'Solidity' },
  { id: 'solana', name: 'Solana', costFactor: 1.3, language: 'Rust' },
  { id: 'polkadot', name: 'Polkadot', costFactor: 1.4, language: 'Rust/Substrate' },
  { id: 'cardano', name: 'Cardano', costFactor: 1.3, language: 'Plutus/Haskell' },
  { id: 'avalanche', name: 'Avalanche', costFactor: 1.1, language: 'Solidity' },
  { id: 'tezos', name: 'Tezos', costFactor: 1.2, language: 'Michelson/LIGO' },
  { id: 'near', name: 'NEAR Protocol', costFactor: 1.2, language: 'AssemblyScript/Rust' },
  { id: 'polygon', name: 'Polygon', costFactor: 1.0, language: 'Solidity' },
  { id: 'tron', name: 'TRON', costFactor: 1.0, language: 'Solidity' },
  { id: 'eos', name: 'EOS', costFactor: 1.1, language: 'C++' },
  { id: 'algorand', name: 'Algorand', costFactor: 1.2, language: 'TEAL/Python' },
  { id: 'hyperledger', name: 'Hyperledger Fabric', costFactor: 1.3, language: 'Go/JavaScript' },
  { id: 'corda', name: 'Corda', costFactor: 1.3, language: 'Kotlin/Java' },
  { id: 'custom', name: 'Собственный блокчейн', costFactor: 1.8, language: 'Разные' }
];

// Размеры проектов
export const projectSizes = [
  { id: 'small', name: 'Малый проект', costFactor: 0.7, description: '1-2 разработчика, до 3 месяцев' },
  { id: 'medium', name: 'Средний проект', costFactor: 1.0, description: '3-5 разработчиков, 3-6 месяцев' },
  { id: 'large', name: 'Крупный проект', costFactor: 1.5, description: '6-10 разработчиков, 6-12 месяцев' },
  { id: 'enterprise', name: 'Корпоративный', costFactor: 2.0, description: 'Команда 10+ человек, 12+ месяцев' }
];

// Уровни сложности
export const complexityLevels = [
  { id: 'low', name: 'Низкая', costFactor: 0.7 },
  { id: 'medium', name: 'Средняя', costFactor: 1.0 },
  { id: 'high', name: 'Высокая', costFactor: 1.5 },
  { id: 'very_high', name: 'Очень высокая', costFactor: 2.0 }
];

// Расположение команды
export const teamLocations = [
  { id: 'eastern_europe', name: 'Восточная Европа', costFactor: 0.8 },
  { id: 'western_europe', name: 'Западная Европа/США', costFactor: 1.7 },
  { id: 'asia', name: 'Азия', costFactor: 0.7 },
  { id: 'mixed', name: 'Смешанная команда', costFactor: 1.0 }
];

/**
 * BlockchainProjectCostCalculatorPage Component - Калькулятор стоимости разработки блокчейн-проектов.
 */
const BlockchainProjectCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('blockchain-cost-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости блокчейн-проекта | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте стоимость разработки блокчейн-проекта с учетом технологического стека, сложности, команды и дополнительных требований. Получите детальную смету по разработке, тестированию, аудиту безопасности и поддержке вашего блокчейн-решения.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [costDetails, setCostDetails] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProjects, setSavedProjects] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных проектов из localStorage ---
  useEffect(() => {
    const savedProjectsData = localStorage.getItem('savedBlockchainProjects');
    if (savedProjectsData) {
      try {
        setSavedProjects(JSON.parse(savedProjectsData));
      } catch (e) {
        console.error("Error loading saved projects:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'dapp':
          newState.projectType = 'dapp';
          break;
        case 'defi':
          newState.projectType = 'defi';
          break;
        case 'nft':
          newState.projectType = 'nft';
          newState.featuresNFT = true;
          newState.includeAdditionalFeatures = true;
          break;
        case 'dao':
          newState.projectType = 'dao';
          newState.featuresDAO = true;
          newState.includeAdditionalFeatures = true;
          break;
        case 'exchange':
          newState.projectType = 'exchange';
          newState.featuresDEX = true;
          newState.includeAdditionalFeatures = true;
          break;
        case 'enterprise':
          newState.projectSize = 'enterprise';
          newState.blockchainPlatform = 'hyperledger';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setCostDetails([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение проекта в localStorage
  const saveProject = (name) => {
    if (!results) return;

    const newSavedProject = {
      id: Date.now().toString(),
      name: name || `${getProjectTypeName(formData.projectType)} на ${getBlockchainPlatformName(formData.blockchainPlatform)}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedProjects = [...savedProjects, newSavedProject];
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedBlockchainProjects', JSON.stringify(updatedSavedProjects));

    return newSavedProject;
  };

  // Удаление сохраненного проекта
  const deleteSavedProject = (id) => {
    const updatedSavedProjects = savedProjects.filter(project => project.id !== id);
    setSavedProjects(updatedSavedProjects);
    localStorage.setItem('savedBlockchainProjects', JSON.stringify(updatedSavedProjects));
  };

  // Загрузка сохраненного проекта
  const loadSavedProject = (id) => {
    const projectToLoad = savedProjects.find(project => project.id === id);
    if (projectToLoad) {
      setFormData(projectToLoad.formData);
      setResults(projectToLoad.results);
      setCostDetails(projectToLoad.results.costBreakdown || []);
    }
  };

  // Вспомогательные функции для получения имени типа проекта и блокчейн-платформы
  const getProjectTypeName = (typeId) => {
    const projectType = projectTypes.find(type => type.id === typeId);
    return projectType ? projectType.name : typeId;
  };

  const getBlockchainPlatformName = (platformId) => {
    const platform = blockchainPlatforms.find(p => p.id === platformId);
    return platform ? platform.name : platformId;
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Database className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости блокчейн-проекта'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              setCostDetails={setCostDetails}
              projectTypes={projectTypes}
              blockchainPlatforms={blockchainPlatforms}
              projectSizes={projectSizes}
              complexityLevels={complexityLevels}
              teamLocations={teamLocations}
              saveProject={saveProject}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <CostBreakdown 
                  costDetails={costDetails} 
                  results={results}
                />
              </>
            )}

            {/* --- Информационный контент о блокчейн-проектах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              projectTypes={projectTypes}
              blockchainPlatforms={blockchainPlatforms}
              projectSizes={projectSizes}
            />

            {/* --- Панель сравнения проектов --- */}
            <ComparisonSidebar 
              savedProjects={savedProjects}
              deleteSavedProject={deleteSavedProject}
              loadSavedProject={loadSavedProject}
              blockchainPlatforms={blockchainPlatforms}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Cost Breakdown Table */
        .cost-breakdown-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .cost-breakdown-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .cost-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .cost-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .cost-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default BlockchainProjectCostCalculatorPage;